import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Handshake,
  Image as ImageIcon,
  Plus,
  Edit,
  Trash2,
  Upload,
  Link as LinkIcon,
  Eye,
  EyeOff,
  MapPin,
  ShoppingBag,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** O endereço completo de uma imagem guardada no servidor. */
export const enderecoDaImagem = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
};

const PARCEIRO_VAZIO = {
  name: '', benefit: '', category: '', description: '',
  logo_url: '', address: '', phone: '', website: '', is_active: true
};

// Um artigo da montra. É o mesmo registo que o Stock usa — há uma só lista
// de artigos. O que esta página acrescenta é mostrar se ele chega mesmo à
// aplicação, que depende da fotografia e do stock.
// Um artigo da montra. Sem tamanho: os tamanhos são coisa do Stock, e aqui
// mostra-se um artigo por cor — nove linhas de capacete davam nove cartões
// iguais, só com a letra diferente.
const ARTIGO_VAZIO = {
  name: '', category: 'clothing', color: '',
  price: '', quantity: '', description: '', photo_url: ''
};

const MEDIA_VAZIA = { kind: 'photo', url: '', caption: '', taken_on: '', is_active: true };

// A ficha do ginásio: o que o sócio vê no separador "O Ginásio"
const CAMPOS_DO_GINASIO = [
  { campo: 'name', etiqueta: 'Nome' },
  { campo: 'address', etiqueta: 'Morada' },
  { campo: 'phone', etiqueta: 'Telefone' },
  { campo: 'email', etiqueta: 'Email' },
  { campo: 'hours', etiqueta: 'Horário', ajuda: 'Ex: Seg a Sex 7h-22h · Sáb 9h-13h' },
  { campo: 'whatsapp', etiqueta: 'WhatsApp', ajuda: 'Só o número, com indicativo. Ex: 351912345678' },
  { campo: 'maps_url', etiqueta: 'Link do Google Maps', ajuda: 'No Maps: Partilhar → Copiar link' },
  { campo: 'review_url', etiqueta: 'Link para avaliações', ajuda: 'No perfil do negócio: Pedir avaliações → Copiar link' },
  { campo: 'instagram', etiqueta: 'Instagram' },
  { campo: 'facebook', etiqueta: 'Facebook' },
];

const AppDoSocio = ({ language }) => {
  const [aba, setAba] = useState('parceiros');
  const [parceiros, setParceiros] = useState([]);
  const [media, setMedia] = useState([]);
  const [aCarregar, setACarregar] = useState(true);

  const [parceiroAberto, setParceiroAberto] = useState(false);
  const [parceiroForm, setParceiroForm] = useState(PARCEIRO_VAZIO);
  const [parceiroAEditar, setParceiroAEditar] = useState(null);

  const [mediaAberta, setMediaAberta] = useState(false);
  const [mediaForm, setMediaForm] = useState(MEDIA_VAZIA);
  const [artigos, setArtigos] = useState([]);
  const [artigoAberto, setArtigoAberto] = useState(false);
  const [artigoForm, setArtigoForm] = useState(ARTIGO_VAZIO);
  const [artigoAEditar, setArtigoAEditar] = useState(null);
  const [aEnviar, setAEnviar] = useState(false);
  const [ginasio, setGinasio] = useState(null);
  const [aGuardarGinasio, setAGuardarGinasio] = useState(false);

  const ficheiroLogo = useRef(null);
  const ficheiroFoto = useRef(null);
  const ficheiroGinasio = useRef(null);
  const ficheiroArtigo = useRef(null);

  const t = {
    pt: {
      titulo: 'Aplicação do Sócio',
      subtitulo: 'O que o sócio vê no telemóvel. O que puser aqui aparece-lhe lá.',
      parceiros: 'Parceiros',
      multimedia: 'Multimédia',
      novoParceiro: 'Novo Parceiro',
      novaFoto: 'Nova Publicação',
      nome: 'Nome',
      beneficio: 'Vantagem para o sócio',
      beneficioAjuda: 'É o que mais conta. Ex: "10% em consultas de fisioterapia"',
      categoria: 'Categoria',
      descricao: 'Descrição',
      logotipo: 'Logótipo',
      morada: 'Morada',
      telefone: 'Telefone',
      site: 'Site',
      visivel: 'Visível na aplicação',
      escolherFicheiro: 'Escolher imagem',
      aEnviar: 'A enviar…',
      guardar: 'Guardar',
      cancelar: 'Cancelar',
      tipo: 'Tipo',
      fotografia: 'Fotografia',
      videoLink: 'Vídeo (link)',
      legenda: 'Legenda',
      data: 'Data',
      linkDoVideo: 'Link do vídeo',
      linkAjuda: 'Cole o endereço do Instagram, YouTube ou Facebook',
      semParceiros: 'Ainda não há parceiros. Carregue em Novo Parceiro para começar.',
      semMedia: 'Ainda não há fotografias nem vídeos publicados.',
      escondido: 'Escondido',
      apagar: 'Apagar',
      editar: 'Editar',
      ginasio: 'O Ginásio',
      ginasioAjuda: 'Contactos, horário e localização. É o que o sócio vê no separador "O Ginásio" da aplicação.',
      sobre: 'Sobre o ginásio',
      fotoDoGinasio: 'Fotografia do ginásio',
      loja: 'Loja',
      novoArtigo: 'Novo Artigo',
      lojaAjuda: 'A montra da aplicação: um artigo por cor. Os tamanhos ficam no Stock e são perguntados ao sócio na altura de reservar. Um artigo só aparece se tiver fotografia e stock.',
      semArtigos: 'Ainda não há artigos. Carregue em Novo Artigo para começar: t-shirts, hoodies, luvas, caneleiras, capacetes, ligaduras…',
      naApp: 'Na aplicação',
      faltaFoto: 'Falta a fotografia',
      semStock: 'Sem stock',
      tamanhos: 'Tamanhos',
      cor: 'Cor',
      preco: 'Preço',
      quantidade: 'Quantidade',
      fotografiaDoArtigo: 'Fotografia do artigo'
    },
    en: {
      titulo: 'Member App',
      subtitulo: 'What the member sees on their phone.',
      parceiros: 'Partners',
      multimedia: 'Media',
      novoParceiro: 'New Partner',
      novaFoto: 'New Post',
      nome: 'Name',
      beneficio: 'Member benefit',
      beneficioAjuda: 'This is what matters most. E.g. "10% off physiotherapy"',
      categoria: 'Category',
      descricao: 'Description',
      logotipo: 'Logo',
      morada: 'Address',
      telefone: 'Phone',
      site: 'Website',
      visivel: 'Visible in the app',
      escolherFicheiro: 'Choose image',
      aEnviar: 'Uploading…',
      guardar: 'Save',
      cancelar: 'Cancel',
      loja: 'Shop',
      novoArtigo: 'New Item',
      tipo: 'Type',
      fotografia: 'Photo',
      videoLink: 'Video (link)',
      legenda: 'Caption',
      data: 'Date',
      linkDoVideo: 'Video link',
      linkAjuda: 'Paste the Instagram, YouTube or Facebook address',
      semParceiros: 'No partners yet.',
      semMedia: 'No photos or videos published yet.',
      escondido: 'Hidden',
      apagar: 'Delete',
      editar: 'Edit',
      ginasio: 'The Gym',
      ginasioAjuda: 'Contacts, hours and location.',
      sobre: 'About the gym',
      fotoDoGinasio: 'Gym photo'
    }
  };
  const txt = t[language] || t.pt;

  useEffect(() => {
    carregarTudo();
  }, []);

  const carregarTudo = async () => {
    try {
      setACarregar(true);
      const [p, m, g, a] = await Promise.all([
        axios.get(`${API}/partners`).catch(() => ({ data: [] })),
        axios.get(`${API}/media`).catch(() => ({ data: [] })),
        axios.get(`${API}/gym-info`).catch(() => ({ data: {} })),
        axios.get(`${API}/inventory`).catch(() => ({ data: [] }))
      ]);
      setParceiros(p.data);
      setMedia(m.data);
      setGinasio(g.data || {});
      setArtigos(a.data);
    } finally {
      setACarregar(false);
    }
  };

  /** Envia a imagem e devolve o endereço onde ficou. */
  const enviarImagem = async (ficheiro) => {
    const corpo = new FormData();
    corpo.append('ficheiro', ficheiro);
    const r = await axios.post(`${API}/uploads`, corpo, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return r.data.url;
  };

  const escolherImagem = async (evento, aoTerminar) => {
    const ficheiro = evento.target.files?.[0];
    if (!ficheiro) return;
    try {
      setAEnviar(true);
      aoTerminar(await enviarImagem(ficheiro));
    } catch (erro) {
      toast.error(erro.response?.data?.detail || 'Não consegui enviar a imagem.');
    } finally {
      setAEnviar(false);
      evento.target.value = '';
    }
  };

  /* ------------------------------------------------------------ parceiros */

  const abrirParceiro = (p) => {
    setParceiroAEditar(p || null);
    setParceiroForm(p ? { ...PARCEIRO_VAZIO, ...p } : PARCEIRO_VAZIO);
    setParceiroAberto(true);
  };

  const guardarParceiro = async (e) => {
    e.preventDefault();
    const corpo = {
      name: parceiroForm.name.trim(),
      benefit: parceiroForm.benefit.trim(),
      category: parceiroForm.category.trim() || null,
      description: parceiroForm.description.trim() || null,
      logo_url: parceiroForm.logo_url || null,
      address: parceiroForm.address.trim() || null,
      phone: parceiroForm.phone.trim() || null,
      website: parceiroForm.website.trim() || null,
      is_active: parceiroForm.is_active
    };
    try {
      if (parceiroAEditar) {
        await axios.put(`${API}/partners/${parceiroAEditar.id}`, corpo);
        toast.success('Parceiro corrigido.');
      } else {
        await axios.post(`${API}/partners`, corpo);
        toast.success('Parceiro criado.');
      }
      setParceiroAberto(false);
      carregarTudo();
    } catch (erro) {
      toast.error(erro.response?.data?.detail || 'Erro ao guardar o parceiro');
    }
  };

  const apagarParceiro = async (p) => {
    if (!window.confirm(`Apagar o parceiro "${p.name}"?`)) return;
    try {
      await axios.delete(`${API}/partners/${p.id}`);
      toast.success('Parceiro eliminado.');
      carregarTudo();
    } catch {
      toast.error('Erro ao eliminar o parceiro');
    }
  };

  /* ----------------------------------------------------------- multimédia */

  const guardarMedia = async (e) => {
    e.preventDefault();
    if (!mediaForm.url) {
      toast.error(mediaForm.kind === 'photo' ? 'Escolha uma fotografia.' : 'Cole o link do vídeo.');
      return;
    }
    try {
      await axios.post(`${API}/media`, {
        kind: mediaForm.kind,
        url: mediaForm.url,
        caption: mediaForm.caption.trim() || null,
        taken_on: mediaForm.taken_on || null,
        is_active: mediaForm.is_active
      });
      toast.success('Publicado na aplicação.');
      setMediaAberta(false);
      setMediaForm(MEDIA_VAZIA);
      carregarTudo();
    } catch (erro) {
      toast.error(erro.response?.data?.detail || 'Erro ao publicar');
    }
  };

  const apagarMedia = async (m) => {
    if (!window.confirm('Apagar esta publicação?')) return;
    try {
      await axios.delete(`${API}/media/${m.id}`);
      toast.success('Publicação eliminada.');
      carregarTudo();
    } catch {
      toast.error('Erro ao eliminar');
    }
  };

  const trocarVisibilidade = async (m) => {
    try {
      await axios.put(`${API}/media/${m.id}`, {
        kind: m.kind, url: m.url, caption: m.caption,
        taken_on: m.taken_on, is_active: !m.is_active
      });
      carregarTudo();
    } catch {
      toast.error('Erro ao alterar');
    }
  };

  /* ---------------------------------------------------------------- loja */

  /**
   * Os artigos juntos por nome e cor, como a montra os mostra.
   *
   * O Stock desdobra cada modelo por tamanho — nove linhas só para os
   * capacetes. Na montra isso davam nove cartões iguais, só com a letra
   * diferente. Os tamanhos ficam no Stock e perguntam-se na reserva.
   */
  const gruposDeArtigos = () => {
    const grupos = new Map();
    artigos.forEach((a) => {
      const nome = (a.name || '').trim();
      const cor = (a.color || '').trim();
      const chave = `${nome.toLowerCase()}|${cor.toLowerCase()}`;
      const g = grupos.get(chave) || {
        chave, name: nome, color: cor, price: 0, description: null,
        photo_url: null, category: a.category, linhas: []
      };
      g.price = Math.max(g.price, Number(a.price) || 0);
      if (!g.photo_url && a.photo_url) g.photo_url = a.photo_url;
      if (!g.description && a.description) g.description = a.description;
      g.linhas.push(a);
      grupos.set(chave, g);
    });
    return [...grupos.values()].sort((x, y) =>
      `${x.name} ${x.color}`.localeCompare(`${y.name} ${y.color}`, 'pt'));
  };

  /**
   * Porque é que um artigo não chega à aplicação.
   *
   * A montra do sócio só mostra o que tem fotografia e stock (ver
   * `GET /api/mobile/shop`). Sem isto, um artigo criado ficava invisível e
   * ninguém percebia porquê.
   */
  const estadoDoArtigo = (g) => {
    if (!g.photo_url) return { ok: false, texto: txt.faltaFoto, cor: '#dc2626' };
    const stock = g.linhas.reduce((t, a) => t + (Number(a.quantity) || 0), 0);
    if (stock <= 0) return { ok: false, texto: txt.semStock, cor: '#dc2626' };
    return { ok: true, texto: txt.naApp, cor: '#16a34a' };
  };

  const abrirArtigo = (g) => {
    setArtigoAEditar(g || null);
    setArtigoForm(g
      ? {
          ...ARTIGO_VAZIO,
          name: g.name, category: g.category || 'clothing', color: g.color,
          description: g.description || '', photo_url: g.photo_url || '',
          price: String(g.price ?? ''), quantity: ''
        }
      : ARTIGO_VAZIO);
    setArtigoAberto(true);
  };

  const guardarArtigo = async (e) => {
    e.preventDefault();
    const comuns = {
      name: artigoForm.name.trim(),
      category: artigoForm.category,
      color: artigoForm.color.trim() || null,
      price: parseFloat(artigoForm.price) || 0,
      description: artigoForm.description.trim() || null,
      photo_url: artigoForm.photo_url || null
    };
    try {
      if (artigoAEditar) {
        // O que se escreve aqui vale para todos os tamanhos desta cor; o
        // tamanho e a quantidade de cada um ficam como estão, que são do Stock.
        await Promise.all(artigoAEditar.linhas.map((a) =>
          axios.put(`${API}/inventory/${a.id}`, {
            ...comuns, size: a.size || null, quantity: a.quantity
          })));
        toast.success('Artigo guardado.');
      } else {
        await axios.post(`${API}/inventory`, {
          ...comuns, size: null, quantity: parseInt(artigoForm.quantity, 10) || 0
        });
        toast.success('Artigo criado.');
      }
      setArtigoAberto(false);
      carregarTudo();
    } catch (erro) {
      toast.error(erro.response?.data?.detail || 'Erro ao guardar');
    }
  };

  const apagarArtigo = async (g) => {
    const quantos = g.linhas.length;
    const aviso = quantos > 1
      ? `Apagar "${g.name}${g.color ? ` · ${g.color}` : ''}"? São ${quantos} tamanhos no Stock, e vão todos.`
      : `Apagar "${g.name}"? Deixa de aparecer no Stock e na aplicação.`;
    if (!window.confirm(aviso)) return;
    try {
      await Promise.all(g.linhas.map((a) => axios.delete(`${API}/inventory/${a.id}`)));
      toast.success(quantos > 1 ? `${quantos} tamanhos eliminados.` : 'Artigo eliminado.');
      carregarTudo();
    } catch {
      toast.error('Erro ao eliminar');
    }
  };

  const guardarGinasio = async (e) => {
    e.preventDefault();
    try {
      setAGuardarGinasio(true);
      const corpo = {};
      CAMPOS_DO_GINASIO.forEach(({ campo }) => {
        corpo[campo] = (ginasio[campo] || '').trim();
      });
      corpo.about = (ginasio.about || '').trim();
      corpo.photo_url = ginasio.photo_url || '';
      await axios.put(`${API}/gym-info`, corpo);
      toast.success('Ficha do ginásio guardada.');
      carregarTudo();
    } catch (erro) {
      toast.error(erro.response?.data?.detail || 'Erro ao guardar');
    } finally {
      setAGuardarGinasio(false);
    }
  };

  /* ---------------------------------------------------------------- ecrã */

  const Separador = ({ id, icone: Icone, texto, quantos }) => (
    <button
      type="button"
      onClick={() => setAba(id)}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
        aba === id ? 'text-white' : 'hover:opacity-80'
      }`}
      style={{
        backgroundColor: aba === id ? 'var(--ko-primary-orange)' : 'var(--background-elevated)',
        color: aba === id ? 'white' : 'var(--text-primary)'
      }}
      data-testid={`aba-${id}`}
    >
      <Icone size={16} />
      {texto}
      <span className="text-xs opacity-80">({quantos})</span>
    </button>
  );

  return (
    <div className="p-4 sm:p-6 space-y-6 fade-in">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{txt.titulo}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{txt.subtitulo}</p>
        </div>
        {aba !== 'ginasio' && (
          <Button
            onClick={() => {
              if (aba === 'parceiros') return abrirParceiro(null);
              if (aba === 'loja') return abrirArtigo(null);
              return setMediaAberta(true);
            }}
            className="btn-hover"
            data-testid="novo-item"
          >
            <Plus className="mr-2" size={16} />
            {aba === 'parceiros' ? txt.novoParceiro : aba === 'loja' ? txt.novoArtigo : txt.novaFoto}
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <Separador id="parceiros" icone={Handshake} texto={txt.parceiros} quantos={parceiros.length} />
        <Separador id="multimedia" icone={ImageIcon} texto={txt.multimedia} quantos={media.length} />
        <Separador id="loja" icone={ShoppingBag} texto={txt.loja} quantos={gruposDeArtigos().length} />
        <button
          type="button"
          onClick={() => setAba('ginasio')}
          className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200"
          style={{
            backgroundColor: aba === 'ginasio' ? 'var(--ko-primary-orange)' : 'var(--background-elevated)',
            color: aba === 'ginasio' ? 'white' : 'var(--text-primary)'
          }}
          data-testid="aba-ginasio"
        >
          <MapPin size={16} />
          {txt.ginasio}
        </button>
      </div>

      {aCarregar ? (
        <Card><CardContent className="p-12 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500 mx-auto" />
        </CardContent></Card>
      ) : aba === 'parceiros' ? (
        parceiros.length === 0 ? (
          <Card><CardContent className="p-12 text-center text-gray-500 dark:text-gray-400">
            {txt.semParceiros}
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {parceiros.map((p) => (
              <Card key={p.id} className="card-shadow" data-testid={`parceiro-${p.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    {p.logo_url ? (
                      <img
                        src={enderecoDaImagem(p.logo_url)}
                        alt={p.name}
                        className="w-14 h-14 rounded-lg object-cover shrink-0"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-lg shrink-0 flex items-center justify-center"
                           style={{ background: 'var(--background-elevated)' }}>
                        <Handshake size={22} className="text-gray-400" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                        {p.name}
                      </p>
                      <p className="text-sm" style={{ color: 'var(--ko-primary-orange)' }}>
                        {p.benefit}
                      </p>
                      {p.category && (
                        <Badge variant="outline" className="mt-1 text-xs">{p.category}</Badge>
                      )}
                      {!p.is_active && (
                        <Badge variant="outline" className="mt-1 ml-1 text-xs text-gray-500">
                          {txt.escondido}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {(p.address || p.phone) && (
                    <p className="text-xs mt-3 truncate" style={{ color: 'var(--text-secondary)' }}>
                      {[p.address, p.phone].filter(Boolean).join(' · ')}
                    </p>
                  )}

                  <div className="flex justify-end gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={() => abrirParceiro(p)}
                            data-testid={`editar-parceiro-${p.id}`}>
                      <Edit size={14} />
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700"
                            onClick={() => apagarParceiro(p)} data-testid={`apagar-parceiro-${p.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      ) : aba === 'loja' ? (
        <>
          <p className="text-sm text-gray-500 dark:text-gray-400 -mt-2">{txt.lojaAjuda}</p>
          {artigos.length === 0 ? (
            <Card><CardContent className="p-12 text-center text-gray-500 dark:text-gray-400">
              {txt.semArtigos}
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {gruposDeArtigos().map((g) => {
                const estado = estadoDoArtigo(g);
                const tamanhos = g.linhas.map((a) => a.size).filter(Boolean);
                return (
                  <Card key={g.chave} className="card-shadow overflow-hidden" data-testid={`artigo-${g.chave}`}>
                    {g.photo_url ? (
                      <img
                        src={enderecoDaImagem(g.photo_url)}
                        alt={g.name}
                        className="w-full h-40 object-cover"
                      />
                    ) : (
                      <div className="w-full h-40 flex flex-col items-center justify-center gap-2"
                           style={{ background: 'var(--background-elevated)' }}>
                        <ShoppingBag size={28} className="text-gray-400" />
                        <span className="text-xs text-gray-400">{txt.faltaFoto}</span>
                      </div>
                    )}
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                            {g.name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                            {g.color || '—'}
                          </p>
                        </div>
                        <p className="font-bold shrink-0" style={{ color: 'var(--ko-primary-orange)' }}>
                          {Number(g.price || 0).toFixed(2)} €
                        </p>
                      </div>

                      <div className="flex items-center justify-between gap-2 mt-3">
                        <Badge
                          variant="outline"
                          className="text-xs font-normal"
                          style={{ borderColor: estado.cor, color: estado.cor }}
                          data-testid={`estado-artigo-${g.chave}`}
                        >
                          {estado.ok ? <Eye size={12} className="mr-1" /> : <AlertCircle size={12} className="mr-1" />}
                          {estado.texto}
                        </Badge>
                        {/* Os tamanhos são do Stock; aqui só se diz quais há,
                            para se saber o que o sócio vai poder escolher. */}
                        {tamanhos.length > 0 && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {txt.tamanhos}: {tamanhos.join(', ')}
                          </span>
                        )}
                      </div>

                      <div className="flex gap-2 mt-3">
                        <Button variant="outline" size="sm" className="flex-1"
                                onClick={() => abrirArtigo(g)} data-testid={`editar-artigo-${g.chave}`}>
                          <Edit size={14} className="mr-1" /> {txt.editar}
                        </Button>
                        <Button variant="outline" size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => apagarArtigo(g)} data-testid={`apagar-artigo-${g.chave}`}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      ) : aba === 'ginasio' ? (
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle className="flex items-center text-base">
              <MapPin className="mr-2" size={18} />
              {txt.ginasio}
            </CardTitle>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{txt.ginasioAjuda}</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={guardarGinasio} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {CAMPOS_DO_GINASIO.map(({ campo, etiqueta, ajuda }) => (
                  <div key={campo}>
                    <Label htmlFor={`g-${campo}`}>{etiqueta}</Label>
                    <Input
                      id={`g-${campo}`}
                      value={(ginasio && ginasio[campo]) || ''}
                      onChange={(e) => setGinasio({ ...ginasio, [campo]: e.target.value })}
                      data-testid={`ginasio-${campo}`}
                    />
                    {ajuda && (
                      <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{ajuda}</p>
                    )}
                  </div>
                ))}
              </div>

              <div>
                <Label htmlFor="g-about">{txt.sobre}</Label>
                <Textarea
                  id="g-about"
                  rows={3}
                  value={(ginasio && ginasio.about) || ''}
                  onChange={(e) => setGinasio({ ...ginasio, about: e.target.value })}
                  data-testid="ginasio-about"
                />
              </div>

              <div>
                <Label>{txt.fotoDoGinasio}</Label>
                <div className="flex items-center gap-3 mt-1">
                  {ginasio && ginasio.photo_url && (
                    <img src={enderecoDaImagem(ginasio.photo_url)} alt=""
                         className="w-24 h-24 rounded-lg object-cover" />
                  )}
                  <input ref={ficheiroGinasio} type="file" accept="image/*" className="hidden"
                         onChange={(e) => escolherImagem(e, (url) =>
                           setGinasio((g) => ({ ...g, photo_url: url })))}
                         data-testid="ginasio-foto-input" />
                  <Button type="button" variant="outline" disabled={aEnviar}
                          onClick={() => ficheiroGinasio.current?.click()}
                          data-testid="ginasio-foto-btn">
                    <Upload className="mr-2" size={14} />
                    {aEnviar ? txt.aEnviar : txt.escolherFicheiro}
                  </Button>
                  {ginasio && ginasio.photo_url && (
                    <Button type="button" variant="ghost" size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => setGinasio({ ...ginasio, photo_url: '' })}>
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button type="submit" disabled={aGuardarGinasio} data-testid="guardar-ginasio">
                  {txt.guardar}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : media.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-gray-500 dark:text-gray-400">
          {txt.semMedia}
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
          {media.map((m) => (
            <Card key={m.id} className="card-shadow overflow-hidden" data-testid={`media-${m.id}`}>
              <div className="aspect-square relative" style={{ background: 'var(--background-elevated)' }}>
                {m.kind === 'photo' ? (
                  <img src={enderecoDaImagem(m.url)} alt={m.caption || ''}
                       className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-2 p-3">
                    <LinkIcon size={28} className="text-gray-400" />
                    <p className="text-xs text-center break-all" style={{ color: 'var(--text-secondary)' }}>
                      {m.url.replace(/^https?:\/\//, '').slice(0, 40)}
                    </p>
                  </div>
                )}
                {!m.is_active && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <Badge variant="outline" className="text-white border-white">{txt.escondido}</Badge>
                  </div>
                )}
              </div>
              <CardContent className="p-3">
                {m.caption && (
                  <p className="text-sm truncate" style={{ color: 'var(--text-primary)' }}>{m.caption}</p>
                )}
                <div className="flex justify-end gap-1 mt-2">
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0"
                          title={m.is_active ? txt.escondido : txt.visivel}
                          onClick={() => trocarVisibilidade(m)}
                          data-testid={`visibilidade-${m.id}`}>
                    {m.is_active ? <Eye size={14} /> : <EyeOff size={14} />}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                          onClick={() => apagarMedia(m)} data-testid={`apagar-media-${m.id}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Janela do parceiro */}
      <Dialog open={parceiroAberto} onOpenChange={setParceiroAberto}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{parceiroAEditar ? txt.editar : txt.novoParceiro}</DialogTitle>
          </DialogHeader>
          <form onSubmit={guardarParceiro} className="space-y-4">
            <div>
              <Label htmlFor="p-nome">{txt.nome} *</Label>
              <Input id="p-nome" value={parceiroForm.name} required
                     onChange={(e) => setParceiroForm({ ...parceiroForm, name: e.target.value })}
                     data-testid="parceiro-nome" />
            </div>
            <div>
              <Label htmlFor="p-beneficio">{txt.beneficio} *</Label>
              <Input id="p-beneficio" value={parceiroForm.benefit} required
                     placeholder="10% em consultas de fisioterapia"
                     onChange={(e) => setParceiroForm({ ...parceiroForm, benefit: e.target.value })}
                     data-testid="parceiro-beneficio" />
              <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                {txt.beneficioAjuda}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="p-categoria">{txt.categoria}</Label>
                <Input id="p-categoria" value={parceiroForm.category} placeholder="Saúde"
                       onChange={(e) => setParceiroForm({ ...parceiroForm, category: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="p-telefone">{txt.telefone}</Label>
                <Input id="p-telefone" value={parceiroForm.phone}
                       onChange={(e) => setParceiroForm({ ...parceiroForm, phone: e.target.value })} />
              </div>
            </div>
            <div>
              <Label htmlFor="p-morada">{txt.morada}</Label>
              <Input id="p-morada" value={parceiroForm.address}
                     onChange={(e) => setParceiroForm({ ...parceiroForm, address: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="p-site">{txt.site}</Label>
              <Input id="p-site" value={parceiroForm.website} placeholder="https://"
                     onChange={(e) => setParceiroForm({ ...parceiroForm, website: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="p-descricao">{txt.descricao}</Label>
              <Textarea id="p-descricao" rows={3} value={parceiroForm.description}
                        onChange={(e) => setParceiroForm({ ...parceiroForm, description: e.target.value })} />
            </div>

            <div>
              <Label>{txt.logotipo}</Label>
              <div className="flex items-center gap-3 mt-1">
                {parceiroForm.logo_url && (
                  <img src={enderecoDaImagem(parceiroForm.logo_url)} alt=""
                       className="w-16 h-16 rounded-lg object-cover" />
                )}
                <input ref={ficheiroLogo} type="file" accept="image/*" className="hidden"
                       onChange={(e) => escolherImagem(e, (url) =>
                         setParceiroForm((f) => ({ ...f, logo_url: url })))}
                       data-testid="parceiro-logo-input" />
                <Button type="button" variant="outline" disabled={aEnviar}
                        onClick={() => ficheiroLogo.current?.click()}
                        data-testid="parceiro-logo-btn">
                  <Upload className="mr-2" size={14} />
                  {aEnviar ? txt.aEnviar : txt.escolherFicheiro}
                </Button>
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={parceiroForm.is_active}
                     onChange={(e) => setParceiroForm({ ...parceiroForm, is_active: e.target.checked })} />
              <span className="text-sm" style={{ color: 'var(--text-primary)' }}>{txt.visivel}</span>
            </label>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setParceiroAberto(false)}>
                {txt.cancelar}
              </Button>
              <Button type="submit" data-testid="guardar-parceiro">{txt.guardar}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Janela do artigo. Escreve na mesma lista que o Stock usa: há uma só
          lista de artigos, e o que se criar aqui aparece lá e vice-versa. */}
      <Dialog open={artigoAberto} onOpenChange={setArtigoAberto}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{artigoAEditar ? txt.editar : txt.novoArtigo}</DialogTitle>
          </DialogHeader>
          {artigoAEditar && artigoAEditar.linhas.length > 1 && (
            <p className="text-sm text-amber-600 dark:text-amber-400">
              Este artigo tem {artigoAEditar.linhas.length} tamanhos no Stock
              ({artigoAEditar.linhas.map((a) => a.size).filter(Boolean).join(', ')}).
              O que guardar aqui vale para todos.
            </p>
          )}
          <form onSubmit={guardarArtigo} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>{txt.nome} *</Label>
                <Input
                  value={artigoForm.name}
                  onChange={(e) => setArtigoForm({ ...artigoForm, name: e.target.value })}
                  placeholder="T-shirt KO, Luvas de Boxe, Caneleiras…"
                  required
                  data-testid="artigo-nome"
                />
              </div>
              <div>
                <Label>{txt.categoria} *</Label>
                <Select
                  value={artigoForm.category}
                  onValueChange={(v) => setArtigoForm({ ...artigoForm, category: v })}
                >
                  <SelectTrigger data-testid="artigo-categoria"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="clothing">Vestuário</SelectItem>
                    <SelectItem value="equipment">Equipamento</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{txt.cor}</Label>
                <Input
                  value={artigoForm.color}
                  onChange={(e) => setArtigoForm({ ...artigoForm, color: e.target.value })}
                  placeholder="Preto, Vermelho…"
                  data-testid="artigo-cor"
                />
              </div>
              <div>
                <Label>{txt.preco} (€) *</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={artigoForm.price}
                  onChange={(e) => setArtigoForm({ ...artigoForm, price: e.target.value })}
                  required
                  data-testid="artigo-preco"
                />
              </div>
              {/* A quantidade só se pergunta ao criar. Num artigo que já
                  existe ela é de cada tamanho, e isso é do Stock. */}
              {!artigoAEditar && (
                <div>
                  <Label>{txt.quantidade} *</Label>
                  <Input
                    type="number" min="0"
                    value={artigoForm.quantity}
                    onChange={(e) => setArtigoForm({ ...artigoForm, quantity: e.target.value })}
                    required
                    data-testid="artigo-quantidade"
                  />
                </div>
              )}
            </div>

            <div>
              <Label>{txt.descricao}</Label>
              <Textarea
                value={artigoForm.description}
                onChange={(e) => setArtigoForm({ ...artigoForm, description: e.target.value })}
                rows={2}
                data-testid="artigo-descricao"
              />
            </div>

            <div>
              <Label>{txt.fotografiaDoArtigo}</Label>
              <div className="flex items-center gap-3 mt-1">
                {artigoForm.photo_url && (
                  <img
                    src={enderecoDaImagem(artigoForm.photo_url)}
                    alt=""
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                )}
                <input
                  type="file" accept="image/*" hidden ref={ficheiroArtigo}
                  onChange={(e) => escolherImagem(e, (url) => setArtigoForm((f) => ({ ...f, photo_url: url })))}
                />
                <Button type="button" variant="outline" disabled={aEnviar}
                        onClick={() => ficheiroArtigo.current?.click()}
                        data-testid="artigo-foto">
                  <Upload size={15} className="mr-2" />
                  {aEnviar ? txt.aEnviar : txt.escolherFicheiro}
                </Button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Sem fotografia o artigo não aparece na montra da aplicação.
                É a mesma para todos os tamanhos desta cor.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setArtigoAberto(false)}>
                {txt.cancelar}
              </Button>
              <Button type="submit" className="btn-hover" data-testid="guardar-artigo">
                {txt.guardar}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Janela da multimédia */}
      <Dialog open={mediaAberta} onOpenChange={setMediaAberta}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{txt.novaFoto}</DialogTitle>
          </DialogHeader>
          <form onSubmit={guardarMedia} className="space-y-4">
            <div>
              <Label>{txt.tipo}</Label>
              <Select value={mediaForm.kind}
                      onValueChange={(v) => setMediaForm({ ...mediaForm, kind: v, url: '' })}>
                <SelectTrigger data-testid="media-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="photo">{txt.fotografia}</SelectItem>
                  <SelectItem value="video_link">{txt.videoLink}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {mediaForm.kind === 'photo' ? (
              <div>
                <Label>{txt.fotografia} *</Label>
                <div className="flex items-center gap-3 mt-1">
                  {mediaForm.url && (
                    <img src={enderecoDaImagem(mediaForm.url)} alt=""
                         className="w-20 h-20 rounded-lg object-cover" />
                  )}
                  <input ref={ficheiroFoto} type="file" accept="image/*" className="hidden"
                         onChange={(e) => escolherImagem(e, (url) =>
                           setMediaForm((f) => ({ ...f, url })))}
                         data-testid="media-foto-input" />
                  <Button type="button" variant="outline" disabled={aEnviar}
                          onClick={() => ficheiroFoto.current?.click()}
                          data-testid="media-foto-btn">
                    <Upload className="mr-2" size={14} />
                    {aEnviar ? txt.aEnviar : txt.escolherFicheiro}
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <Label htmlFor="m-link">{txt.linkDoVideo} *</Label>
                <Input id="m-link" value={mediaForm.url} placeholder="https://instagram.com/p/..."
                       onChange={(e) => setMediaForm({ ...mediaForm, url: e.target.value })}
                       data-testid="media-link" />
                <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                  {txt.linkAjuda}
                </p>
              </div>
            )}

            <div>
              <Label htmlFor="m-legenda">{txt.legenda}</Label>
              <Input id="m-legenda" value={mediaForm.caption} placeholder="Aula de Boxe de sábado"
                     onChange={(e) => setMediaForm({ ...mediaForm, caption: e.target.value })}
                     data-testid="media-legenda" />
            </div>
            <div>
              <Label htmlFor="m-data">{txt.data}</Label>
              <Input id="m-data" type="date" value={mediaForm.taken_on}
                     onChange={(e) => setMediaForm({ ...mediaForm, taken_on: e.target.value })} />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setMediaAberta(false)}>
                {txt.cancelar}
              </Button>
              <Button type="submit" disabled={aEnviar} data-testid="guardar-media">{txt.guardar}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppDoSocio;
